import React, { useState } from 'react';
import {
  Send,
  Compass,
  FolderKanban,
  Headphones,
  ArrowRight,
  Database,
  Megaphone,
  BadgeDollarSign,
  LifeBuoy,
  Workflow,
  Brain,
  Clock,
  Archive,
  Users,
  Link2,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConsultantChatGate from '../components/ConsultantChatGate';
import TaskCTA from '../components/TaskCTA';
import RequestCalculationForm from '../components/RequestCalculationForm';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import LandingScrollReveal from '../components/landing/LandingScrollReveal';
import LandingVideoCase from '../components/landing/LandingVideoCase';
import LandingHeroWaveBackground from '../components/landing/LandingHeroWaveBackground';
import LandingIntegrationsMarquee from '../components/landing/LandingIntegrationsMarquee';
import ClientLogosMarquee from '../components/ClientLogosMarquee';
import { PAGE_SEO } from '../data/pageSeo';
import {
  AI_AGENTS_PLATFORM_PAGE,
  buildAiAgentsPlatformStructuredData,
  calculateMonthlySavings,
  formatRubles,
  getRoleMonthlyValue,
} from '../data/aiAgentsPlatformPage';
import { Link } from 'react-router-dom';

const AGENT_ICONS = {
  Send,
  Compass,
  FolderKanban,
  Headphones,
};

const TEAM_TASK_ICONS = {
  Megaphone,
  BadgeDollarSign,
  LifeBuoy,
  Workflow,
};

const ChapterLabel = ({ children, light = false }) => (
  <p
    className={`font-even-mono text-xs md:text-sm uppercase tracking-[0.2em] mb-4 md:mb-6 ${
      light ? 'text-white/60' : 'text-accent'
    }`}
  >
    {children}
  </p>
);

const AiAgentsPlatformPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const seo = PAGE_SEO[AI_AGENTS_PLATFORM_PAGE.path];
  const page = AI_AGENTS_PLATFORM_PAGE;
  const structuredData = buildAiAgentsPlatformStructuredData();
  const monthlySavings = calculateMonthlySavings(page.roi);

  const openForm = () => setIsFormOpen(true);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        keywords={seo.keywords}
        structuredData={structuredData}
      />
      <Header />

      <main className="flex-1 pt-16">
        {/* Hero */}
        <section className="relative min-h-[90vh] flex flex-col text-white">
          <LandingHeroWaveBackground />
          <div className="flex-1 flex items-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 relative z-10 w-full">
            <LandingScrollReveal>
              <ChapterLabel light>{page.hero.chapter}</ChapterLabel>
              <div className="relative max-w-5xl">
                <div className="absolute -top-2 sm:top-0 right-0 sm:right-2 md:right-6 z-20 rotate-45 origin-center">
                  <div className="relative bg-white rounded-xl px-3.5 py-2.5 sm:px-4 sm:py-3 shadow-[0_12px_40px_rgba(0,0,0,0.35)] ring-1 ring-black/5">
                    <div className="bg-white rounded-md px-2 py-1">
                      <img
                        src={page.hero.itmoSticker.logo}
                        alt={page.hero.itmoSticker.logoAlt}
                        className="h-8 sm:h-9 md:h-10 w-auto max-w-[160px] object-contain mx-auto block"
                      />
                    </div>
                    <p className="mt-1.5 text-[10px] sm:text-xs font-medium text-design-black text-center leading-snug whitespace-nowrap">
                      {page.hero.itmoSticker.caption}
                    </p>
                  </div>
                </div>
                <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-[0.95] tracking-tight whitespace-pre-line pr-24 sm:pr-32 md:pr-40">
                  {page.hero.title}
                </h1>
              </div>
              <p className="mt-8 text-xl md:text-2xl text-white/80 max-w-xl font-light">
                {page.hero.subtitle}
              </p>
              <Button
                size="lg"
                className="mt-12 h-14 px-8 text-base bg-white text-design-black hover:bg-white/90"
                onClick={openForm}
              >
                {page.hero.cta}
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden />
              </Button>
            </LandingScrollReveal>
          </div>
          <div className="relative z-10 w-full border-t border-white/10 pt-8 pb-10 md:pb-14">
            <p className="text-center font-even-mono text-xs uppercase tracking-[0.2em] text-white/40 mb-6">
              Опыт с 2017 года
            </p>
            <ClientLogosMarquee variant="on-dark" />
          </div>
        </section>

        {/* Problem */}
        <section className="min-h-[70vh] flex items-center py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <LandingScrollReveal>
              <ChapterLabel>{page.problem.chapter}</ChapterLabel>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] whitespace-pre-line max-w-4xl">
                {page.problem.title}
              </h2>
            </LandingScrollReveal>
            <ul className="mt-16 md:mt-24 space-y-6 md:space-y-8">
              {page.problem.lines.map((line, i) => (
                <LandingScrollReveal key={line} delay={i * 100} as="li">
                  <p className="text-2xl md:text-3xl lg:text-4xl text-muted-foreground font-light border-l-4 border-accent pl-6 md:pl-8">
                    {line}
                  </p>
                </LandingScrollReveal>
              ))}
            </ul>
          </div>
        </section>

        {/* Foundation — RAG */}
        <section className="relative text-white overflow-hidden">
          <div className="bg-design-black pt-20 md:pt-28 pb-14 md:pb-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <LandingScrollReveal>
                <ChapterLabel light>{page.foundation.chapter}</ChapterLabel>
                <div className="flex flex-wrap items-end gap-4">
                  <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] whitespace-pre-line">
                    {page.foundation.title}
                  </h2>
                  <span className="font-even-mono text-sm md:text-base px-3 py-1 rounded-full border border-white/30 text-white/80 mb-2">
                    {page.foundation.tag}
                  </span>
                </div>
              </LandingScrollReveal>
            </div>
          </div>

          <div className="gradient-deeptech py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                <LandingScrollReveal>
                  <p className="text-xl md:text-2xl text-white/85 max-w-lg leading-relaxed">
                    {page.foundation.lead}
                  </p>
                  <ul className="mt-10 space-y-4">
                    {page.foundation.bullets.map((bullet, i) => (
                      <li
                        key={bullet}
                        className="flex items-center gap-3 text-lg text-white/90 animate-slide-up"
                        style={{ animationDelay: `${i * 80}ms` }}
                      >
                        <Database className="h-5 w-5 text-white shrink-0" aria-hidden />
                        {bullet}
                      </li>
                    ))}
                  </ul>
                </LandingScrollReveal>
                <LandingScrollReveal delay={150} className="relative">
                  <div className="aspect-square max-w-lg mx-auto lg:ml-auto rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
                    <img
                      src={page.foundation.image}
                      alt={page.foundation.imageAlt}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </LandingScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* Memory & hybrid team */}
        <section className="py-24 md:py-32 bg-muted/30 border-y border-border/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LandingScrollReveal>
              <ChapterLabel>{page.memoryTeam.chapter}</ChapterLabel>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold whitespace-pre-line leading-[1.02] max-w-4xl">
                {page.memoryTeam.title}
              </h2>
            </LandingScrollReveal>

            <LandingScrollReveal delay={80} className="mt-16 md:mt-20">
              <h3 className="text-2xl md:text-3xl font-bold mb-8 md:mb-10">
                {page.memoryTeam.memory.title}
              </h3>
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                {page.memoryTeam.memory.layers.map((layer, i) => {
                  const LayerIcon = i === 0 ? Clock : Archive;
                  return (
                    <div
                      key={layer.id}
                      className="rounded-2xl bg-background p-8 md:p-10 ring-1 ring-border/60 shadow-sm"
                    >
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl gradient-deeptech mb-6">
                        <LayerIcon className="h-6 w-6 text-white" aria-hidden />
                      </div>
                      <p className="text-xl md:text-2xl font-semibold">{layer.label}</p>
                      <p className="mt-4 text-lg md:text-xl text-muted-foreground leading-relaxed">
                        {layer.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </LandingScrollReveal>

            <LandingScrollReveal delay={120} className="mt-20 md:mt-28">
              <h3 className="text-2xl md:text-3xl font-bold mb-8 md:mb-10">
                {page.memoryTeam.hybridTeam.title}
              </h3>
              <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                {page.memoryTeam.hybridTeam.functions.map((fn, i) => {
                  const FnIcon = [Users, Brain, Link2][i] || Users;
                  return (
                    <div
                      key={fn.id}
                      className="rounded-2xl bg-design-dark-gray text-white p-8 md:p-10 ring-1 ring-white/10"
                    >
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 mb-6">
                        <FnIcon className="h-6 w-6 text-accent" aria-hidden />
                      </div>
                      <p className="text-xl md:text-2xl font-semibold">{fn.title}</p>
                      <p className="mt-4 text-base md:text-lg text-white/75 leading-relaxed">
                        {fn.text}
                      </p>
                    </div>
                  );
                })}
              </div>
            </LandingScrollReveal>
          </div>
        </section>

        {/* Agents intro */}
        <section className="py-24 md:py-32 border-b border-border/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <LandingScrollReveal>
              <ChapterLabel>{page.agentsIntro.chapter}</ChapterLabel>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold whitespace-pre-line leading-[1.02]">
                {page.agentsIntro.title}
              </h2>
              <p className="mt-6 text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
                {page.agentsIntro.subtitle}
              </p>
            </LandingScrollReveal>
          </div>
        </section>

        {/* Agent blocks */}
        {page.agents.map((agent, index) => {
          const Icon = AGENT_ICONS[agent.icon] || Send;
          const isDark = index % 2 === 1;

          return (
            <section
              key={agent.id}
              id={agent.id}
              className={`min-h-[80vh] flex items-center py-20 md:py-28 ${
                isDark ? 'bg-design-dark-gray text-white' : 'bg-background'
              }`}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                <div
                  className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                    index % 2 === 0 ? '' : 'lg:[&>div:first-child]:order-2'
                  }`}
                >
                  <LandingScrollReveal className="lg:col-span-5">
                    <p
                      className={`font-even-mono text-xs uppercase tracking-widest mb-4 ${
                        isDark ? 'text-white/50' : 'text-muted-foreground'
                      }`}
                    >
                      {agent.chapter}
                    </p>
                    <div
                      className={`inline-flex h-16 w-16 items-center justify-center rounded-2xl mb-8 ${agent.accent}`}
                    >
                      <Icon className="h-8 w-8 text-white" aria-hidden />
                    </div>
                    <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                      {agent.name}
                    </h3>
                    <p
                      className={`mt-3 text-lg md:text-xl font-medium ${
                        isDark ? 'text-accent' : 'text-accent'
                      }`}
                    >
                      {agent.tagline}
                    </p>
                  </LandingScrollReveal>

                  <LandingScrollReveal delay={120} className="lg:col-span-7">
                    <p
                      className={`text-2xl md:text-3xl lg:text-4xl font-light leading-snug ${
                        isDark ? 'text-white/90' : 'text-foreground'
                      }`}
                    >
                      {agent.lead}
                    </p>
                    <p
                      className={`mt-8 text-lg md:text-xl border-l-4 pl-5 ${
                        isDark
                          ? 'border-accent text-white/70'
                          : 'border-accent text-muted-foreground'
                      }`}
                    >
                      {agent.outcome}
                    </p>
                  </LandingScrollReveal>
                </div>
              </div>
            </section>
          );
        })}

        {/* Flow */}
        <section className="py-24 md:py-32 gradient-deeptech text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LandingScrollReveal>
              <ChapterLabel light>{page.flow.chapter}</ChapterLabel>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold whitespace-pre-line leading-[1.02] mb-16 md:mb-24">
                {page.flow.title}
              </h2>
            </LandingScrollReveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {page.flow.steps.map((step, i) => (
                <LandingScrollReveal key={step.label} delay={i * 80}>
                  <div className="rounded-2xl bg-white/10 backdrop-blur-sm p-6 md:p-8 ring-1 ring-white/15 min-h-[140px]">
                    <span className="font-even-mono text-3xl md:text-4xl font-bold text-white/40">
                      {step.label}
                    </span>
                    <p className="mt-4 text-lg md:text-xl font-medium">{step.text}</p>
                  </div>
                </LandingScrollReveal>
              ))}
            </div>
            <LandingScrollReveal delay={200} className="mt-16 md:mt-20">
              <p className="text-xl md:text-2xl text-white/80 max-w-4xl leading-relaxed font-light">
                {page.flow.example}
              </p>
            </LandingScrollReveal>
          </div>
        </section>

        {/* Integrations */}
        <section className="py-24 md:py-32 border-y border-border/50 bg-muted/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LandingScrollReveal>
              <ChapterLabel>{page.integrations.chapter}</ChapterLabel>
              <p className="text-lg md:text-xl text-accent font-medium mb-4">
                {page.integrations.subtitle}
              </p>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold whitespace-pre-line leading-[1.02] max-w-4xl">
                {page.integrations.title}
              </h2>
              <p className="mt-8 text-xl md:text-2xl text-muted-foreground max-w-3xl font-light leading-relaxed">
                {page.integrations.lead}
              </p>
            </LandingScrollReveal>

            <LandingScrollReveal delay={100} className="mt-16 md:mt-20">
              <div className="rounded-3xl gradient-deeptech text-white p-8 md:p-12 lg:p-16 text-center ring-1 ring-white/10">
                <p className="font-even-mono text-xs uppercase tracking-[0.25em] text-white/60 mb-4">
                  Интеграция с
                </p>
                <p className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold tabular-nums leading-none">
                  {page.integrations.serviceCount}
                </p>
                <p className="mt-2 text-2xl md:text-3xl font-medium text-white/90">
                  {page.integrations.serviceCountLabel}
                </p>
              </div>
            </LandingScrollReveal>

            <div className="mt-12 md:mt-16 grid md:grid-cols-3 gap-6 md:gap-8">
              {page.integrations.highlights.map((item, i) => (
                <LandingScrollReveal key={item.title} delay={i * 80}>
                  <div className="rounded-2xl border border-border/60 bg-card p-8 md:p-10 min-h-[220px] hover:border-accent/40 transition-colors h-full">
                    <p className="text-3xl md:text-4xl font-bold text-accent tabular-nums">
                      {item.value}
                    </p>
                    <h3 className="mt-4 text-xl md:text-2xl font-bold">{item.title}</h3>
                    <p className="mt-3 text-muted-foreground leading-relaxed">{item.text}</p>
                  </div>
                </LandingScrollReveal>
              ))}
            </div>

            <LandingScrollReveal delay={150} className="mt-16 md:mt-20">
              <p className="font-even-mono text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6 text-center">
                Популярные системы
              </p>
              <LandingIntegrationsMarquee />
            </LandingScrollReveal>

            <LandingScrollReveal delay={200} className="mt-12 md:mt-16">
              <div className="rounded-2xl border border-border/60 bg-card p-6 md:p-8">
                <p className="text-sm font-medium text-accent">
                  {page.integrations.platform.title}
                </p>
                <p className="mt-2 text-muted-foreground max-w-2xl">
                  {page.integrations.platform.text}
                </p>
              </div>
            </LandingScrollReveal>
          </div>
        </section>

        {/* Team tasks */}
        <section className="py-24 md:py-32 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LandingScrollReveal>
              <ChapterLabel>{page.teamTasks.chapter}</ChapterLabel>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold whitespace-pre-line leading-[1.02] max-w-4xl">
                {page.teamTasks.title}
              </h2>
              <p className="mt-6 text-xl md:text-2xl text-muted-foreground max-w-3xl">
                {page.teamTasks.subtitle}
              </p>
            </LandingScrollReveal>

            <div className="mt-16 md:mt-24 grid md:grid-cols-2 gap-6 md:gap-8">
              {page.teamTasks.items.map((item, i) => {
                const Icon = TEAM_TASK_ICONS[item.icon] || Workflow;

                return (
                  <LandingScrollReveal key={item.id} delay={i * 80}>
                    <article className="rounded-2xl border border-border/60 bg-card p-8 md:p-10 h-full hover:border-accent/40 transition-colors">
                      <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl gradient-deeptech mb-6">
                        <Icon className="h-7 w-7 text-white" aria-hidden />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold">{item.name}</h3>
                      <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                        {item.text}
                      </p>
                    </article>
                  </LandingScrollReveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ROI */}
        <section className="py-24 md:py-32 bg-design-black text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LandingScrollReveal>
              <ChapterLabel light>{page.roi.chapter}</ChapterLabel>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold whitespace-pre-line leading-[1.02]">
                {page.roi.title}
              </h2>
              <p className="mt-8 text-xl md:text-2xl text-white/75 max-w-3xl font-light leading-relaxed">
                {page.roi.lead}
              </p>
            </LandingScrollReveal>

            <div className="mt-16 md:mt-24 grid lg:grid-cols-2 gap-10 lg:gap-16">
              <LandingScrollReveal>
                <p className="font-even-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-6">
                  Экономия по ролям в месяц
                </p>
                <ul className="space-y-4">
                  {page.roi.roles.map((role) => (
                    <li
                      key={role.id}
                      className="flex items-center justify-between gap-4 rounded-2xl bg-white/5 ring-1 ring-white/10 px-6 py-5"
                    >
                      <span className="text-lg md:text-xl text-white/90">{role.label}</span>
                      <span className="text-xl md:text-2xl font-bold tabular-nums shrink-0">
                        {formatRubles(role.monthlyValue)}
                      </span>
                    </li>
                  ))}
                </ul>
              </LandingScrollReveal>

              <LandingScrollReveal delay={120}>
                <p className="font-even-mono text-xs uppercase tracking-[0.2em] text-white/50 mb-6">
                  {page.roi.company.title}
                </p>
                <div className="rounded-3xl bg-white/5 ring-1 ring-white/10 p-6 md:p-8">
                  <ul className="space-y-5">
                    {page.roi.company.headcount.map((row) => {
                      const roleValue = getRoleMonthlyValue(page.roi, row.roleId);
                      const rowTotal = row.count * roleValue;

                      return (
                        <li
                          key={row.roleId}
                          className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-white/10 pb-5 last:border-0 last:pb-0"
                        >
                          <span className="text-white/80">
                            {row.count} × {row.label.toLowerCase()}
                          </span>
                          <span className="text-lg md:text-xl font-semibold tabular-nums">
                            {formatRubles(rowTotal)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="mt-8 pt-8 border-t border-white/15 flex flex-wrap items-end justify-between gap-4">
                    <span className="text-white/60 text-lg">Итого в месяц</span>
                    <span className="text-3xl md:text-4xl lg:text-5xl font-bold tabular-nums text-white">
                      {formatRubles(monthlySavings)}
                    </span>
                  </div>
                </div>
              </LandingScrollReveal>
            </div>

            <LandingScrollReveal delay={200} className="mt-16 md:mt-20">
              <div className="rounded-3xl gradient-deeptech p-8 md:p-12 ring-1 ring-white/15 max-w-3xl">
                <p className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                  {page.roi.payback.headline}
                </p>
                <p className="mt-4 text-lg md:text-xl text-white/75">
                  {page.roi.payback.note}
                </p>
              </div>
            </LandingScrollReveal>
          </div>
        </section>

        {/* Video cases */}
        {!page.cases.hidden && (
        <section className="py-24 md:py-32 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LandingScrollReveal className="mb-16 md:mb-24">
              <ChapterLabel>{page.cases.chapter}</ChapterLabel>
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold whitespace-pre-line leading-[1.02]">
                {page.cases.title}
              </h2>
              <p className="mt-6 text-xl text-muted-foreground max-w-xl">
                {page.cases.subtitle}
              </p>
            </LandingScrollReveal>
            <div className="space-y-20 md:space-y-28">
              {page.cases.items.map((item, i) => (
                <LandingVideoCase
                  key={item.title}
                  title={item.title}
                  caption={item.caption}
                  embedUrl={item.embedUrl || undefined}
                  isMock={item.isMock}
                  poster={item.poster}
                  index={i}
                />
              ))}
            </div>
          </div>
        </section>
        )}

        {/* Deploy */}
        <section className="py-24 md:py-32 border-t border-border/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <LandingScrollReveal>
              <ChapterLabel>{page.deploy.chapter}</ChapterLabel>
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold whitespace-pre-line leading-[1.02]">
                {page.deploy.title}
              </h2>
            </LandingScrollReveal>
            <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {page.deploy.steps.map((step, i) => (
                <LandingScrollReveal key={step.title} delay={i * 70}>
                  <div className="rounded-2xl border border-border/60 bg-card p-8 min-h-[160px] hover:border-accent/40 transition-colors">
                    <h3 className="text-2xl md:text-3xl font-bold">{step.title}</h3>
                    <p className="mt-3 text-lg text-muted-foreground">{step.text}</p>
                  </div>
                </LandingScrollReveal>
              ))}
            </div>
            <LandingScrollReveal delay={200} className="mt-12">
              <Button
                size="lg"
                className="h-14 px-8 bg-accent hover:bg-accent/90"
                onClick={openForm}
              >
                {page.deploy.cta}
              </Button>
            </LandingScrollReveal>
          </div>
        </section>

        {/* Related + FAQ */}
        <section className="py-20 md:py-24 bg-muted/30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <LandingScrollReveal>
              <Link
                to={page.relatedPage.href}
                className="group flex items-center justify-between rounded-2xl border border-border bg-card p-6 md:p-8 hover:border-accent/50 transition-colors mb-16"
              >
                <div>
                  <p className="text-sm text-accent font-medium">{page.relatedPage.label}</p>
                  <p className="mt-2 text-lg text-muted-foreground">{page.relatedPage.text}</p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors shrink-0 ml-4" />
              </Link>
            </LandingScrollReveal>
            <LandingScrollReveal>
              <h2 className="text-2xl md:text-3xl font-bold mb-6">Вопросы</h2>
              <Accordion type="single" collapsible className="w-full">
                {page.faq.map((item, i) => (
                  <AccordionItem key={item.q} value={`faq-${i}`}>
                    <AccordionTrigger className="text-left text-base md:text-lg">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </LandingScrollReveal>
          </div>
        </section>

        <TaskCTA onCalculationClick={openForm} onCallbackClick={openForm} />
      </main>

      <Footer />
      <ConsultantChatGate />
      <RequestCalculationForm open={isFormOpen} onOpenChange={setIsFormOpen} />
    </div>
  );
};

export default AiAgentsPlatformPage;
